import React, { useState } from 'react';
import { useNavigate } from 'react-router';

import Modal from '~/components/modals/modal';
import Button from '~/components/button';
import LazyImg from '~/components/lazy-img';
import { captureException } from '~/utils/exception-tracking';
import { useCartMutations } from '~/features/cart/cart-actions';
import { useModal } from '~/features/modals/modal-actions';
import { useFlashMessage } from '~/features/flash-messages/flash-message-actions';
import {
  refundPolicyRoute,
  subscriptionsRoute,
  skipShipmentPolicyRoute,
} from '~/routes';

import modalStyles from '~/components/modals/skip-shipment/skip-shipment-modal.module.scss';
import styles from '~/components/account/settings/account-settings.module.scss';

const SkipShipmentModal = ({
  onClose,
  isOpen,
  isFlexPay,
  cartUuid,
  shipmentNumber,
  shipmentDate,
}) => (
  <Modal
    isOpen={isOpen}
    onClose={onClose}
    className={modalStyles.skipShipmentModal}
  >
    <Content
      onClose={onClose}
      isFlexPay={isFlexPay}
      cartUuid={cartUuid}
      shipmentNumber={shipmentNumber}
      shipmentDate={shipmentDate}
    />
  </Modal>
);

export default SkipShipmentModal;

const Content = ({
  isFlexPay,
  cartUuid,
  shipmentNumber = '',
  shipmentDate = '',
  onClose,
}) => {
  const { skipShipment } = useCartMutations();
  const { showFlashMessage } = useFlashMessage();
  const { showModal } = useModal();
  const [isSkippingShipment, setIsSkippingShipment] = useState(false);

  const handleSkipShipment = async () => {
    setIsSkippingShipment(true);
    try {
      const skippedShipment = await skipShipment(cartUuid);

      setIsSkippingShipment(false);
      onClose();

      showModal('SKIP_SHIPMENT_CONFIRMATION', {
        isFlexPay,
        shipmentNumber,
        shipmentDate,
        storeCreditValue: skippedShipment?.totalPriceWithTax,
      });
    } catch (err) {
      captureException(new Error('Skip Shipment: Error skipping shipment'), {
        extras: {
          err,
          error: JSON.stringify(err),
          cartUuid: cartUuid,
        },
      });
      onClose();
      showFlashMessage(
        'Oops, there was an issue skipping your shipment. Please try again later.'
      );
      setIsSkippingShipment(false);
    }
  };

  return (
    <div className={modalStyles.skipShipmentContent}>
      <h3 className={styles.shipmentHistoryTextHeading}>
        Shipment{' '}
        {shipmentNumber && (
          <span className={styles.shipmentNumberPill}>{shipmentNumber}</span>
        )}
      </h3>
      <div className={modalStyles.skipShipmentCopyWrapper}>
        <div className={modalStyles.skipShipmentCopy}>
          <h4>Skip a shipment</h4>
          <p>
            By choosing to skip a shipment, you will not receive the contents of
            this upcoming box.
          </p>
          {isFlexPay ? (
            <p>
              You will not be charged for this shipment on your next scheduled
              charge date.
            </p>
          ) : (
            <p>
              Since you chose to pay in full at the beginning of this season,
              the cost of this upcoming shipment will be credited to your
              account as store credit, which will automatically apply to your
              next Sunday order.
            </p>
          )}
        </div>
        <HelpfulResources onClose={onClose} />
      </div>
      <div className={modalStyles.buttonWrapper}>
        <Button
          fullWidthMobile
          onClick={onClose}
          variant="transparentGreen"
          isLoading={isSkippingShipment}
        >
          Cancel
        </Button>
        <Button
          fullWidthMobile
          onClick={handleSkipShipment}
          isLoading={isSkippingShipment}
        >
          Skip shipment
        </Button>
      </div>
    </div>
  );
};

const HelpfulResources = ({ onClose }) => {
  const navigate = useNavigate();
  return (
    <div className={modalStyles.helpfulResourcesWrapper}>
      <h4>Helpful resources</h4>
      <ul className={modalStyles.helpfulResources}>
        <li>
          <LazyImg
            className={modalStyles.documentIcon}
            width="16"
            height="19"
            src="/icons/document.svg"
            alt="helpful resource"
          />
          <Button
            variant="link"
            className={modalStyles.helpfulResourceLink}
            onClick={() => {
              navigate(skipShipmentPolicyRoute());
              onClose();
            }}
          >
            Skip a shipment
          </Button>
        </li>
        <li>
          <LazyImg
            className={modalStyles.documentIcon}
            width="16"
            height="19"
            src="/icons/document.svg"
            alt="helpful resource"
          />
          <Button
            variant="link"
            className={modalStyles.helpfulResourceLink}
            onClick={() => {
              navigate(subscriptionsRoute());
              onClose();
            }}
          >
            Subscription management
          </Button>
        </li>
        <li>
          <LazyImg
            className={modalStyles.documentIcon}
            width="16"
            height="19"
            src="/icons/document.svg"
            alt="helpful resource"
          />
          <Button
            variant="link"
            className={modalStyles.helpfulResourceLink}
            onClick={() => {
              navigate(refundPolicyRoute());
              onClose();
            }}
          >
            Refund policy
          </Button>
        </li>
      </ul>
    </div>
  );
};
